import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import { type Document } from '@contentful/rich-text-types'
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline'
import clsx from 'clsx'
import { trackEvent } from 'fathom-client'
import React from 'react'
import { getRenderOptionsForSpc } from '#app/utils/contentful-renderer.js'
import { trackAdEvent } from '#app/utils/plausible.client.js'
import { useDebounce } from './use-debounce.ts'
import useIdle from './use-idle.ts'
import useIntersection from './use-intersection.ts'

export type SupportedFormats = 'square' | 'landscape' | 'portrait'

export interface SafeAd {
	id: string
	// TODO default or optional? || '', || 0
	landscapeImageHeight?: number
	landscapeImageUrl?: string
	landscapeImageWidth?: number
	portraitImageHeight?: number
	portraitImageUrl?: string
	portraitImageWidth?: number
	squareImageHeight?: number
	squareImageUrl?: string
	squareImageWidth?: number
	summaryOfProductCharacteristics?: Document
	spc?: {
		text: {
			json: Document
		}
		title: string
		hideTitle: boolean
	} | null
	hideSummaryOfProductCharacteristics?: boolean
	delay: number | null
	url: string
	authenticatedTargeting: boolean
	treatmentOverviewSlug?: string
}

const DEFAULT_AD_DELAY = 1000 * 30 // 30 seconds
const AD_IDLE_TIME = 1000 * 60 // 60 seconds

// The first supported format in the array is preferred (prioritized)
function findPreferredImageDimension(
	ad: SafeAd,
	supportedFormats: SupportedFormats[],
) {
	const converted = supportedFormats
		.map(format => {
			const imageUrl = ad[`${format}ImageUrl`] || ''
			const imageHeight = ad[`${format}ImageHeight`] || 0
			const imageWidth = ad[`${format}ImageWidth`] || 0
			return [imageUrl, imageHeight, imageWidth, format]
		})
		.filter(([imageUrl]) => imageUrl) as [
		string,
		number,
		number,
		SupportedFormats,
	][]
	return converted[0] || []
}

function addUtmContent(originalUrl: string, type: string): string {
	const utmTag = 'utm_content'
	const url = new URL(originalUrl)

	// If url is in disallowList, don't add utm_content as it breaks ad
	const disallowList = ['player.vimeo.com']

	if (!url.searchParams.get(utmTag) && !disallowList.includes(url.hostname)) {
		url.searchParams.append(utmTag, type)
	}

	return url.toString()
}

const spcRenderOptions = getRenderOptionsForSpc()

const Spc = ({
	spc,
	paddingX = 'px-4',
}: {
	spc: NonNullable<SafeAd['spc']>
	paddingX?: string
}) => {
	const [showMoreIsVisible, setShowMoreIsVisible] = React.useState(true)
	const showAll = !showMoreIsVisible
	// Disclosure pattern: the toggle buttons control the description region
	// below. `aria-expanded` + `aria-controls` let screen readers announce the
	// collapsed/expanded state and the relationship.
	const descriptionId = React.useId()

	const handleShowAll = (event: React.MouseEvent<HTMLButtonElement>) => {
		event.preventDefault()
		event.stopPropagation()
		setShowMoreIsVisible(false)
	}

	const handleHideAll = (event: React.MouseEvent<HTMLButtonElement>) => {
		event.preventDefault()
		event.stopPropagation()
		setShowMoreIsVisible(true)
	}

	return (
		<div
			className={
				'border-x border-b dark:border-gray-600 dark:bg-gray-800 dark:text-gray-400 lg:dark:border-gray-700'
			}
		>
			<div
				data-nosnippet
				className={clsx('mt-0 bg-white text-sm leading-snug dark:bg-gray-800', {
					'h-[145px]': !showAll,
					'text-slate-800 dark:text-gray-100': showAll,
				})}
			>
				<div
					className={clsx(paddingX, {
						'flex h-full flex-col': !showAll,
					})}
				>
					{!spc.hideTitle && (
						<p className="mb-0.5 flex-shrink-0 pt-1.5 font-bold">{spc.title}</p>
					)}

					<div
						id={descriptionId}
						itemProp="description"
						className={clsx('my-1 space-y-2 [&_a]:text-blue-600', {
							'relative min-h-0 flex-1 overflow-hidden': !showAll,
						})}
					>
						{documentToReactComponents(spc.text.json, spcRenderOptions)}
						{!showAll && (
							<div className="pointer-events-none absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-white dark:from-gray-800" />
						)}
					</div>
					{showMoreIsVisible && (
						<button
							type="button"
							aria-expanded={showAll}
							aria-controls={descriptionId}
							className="-ml-3 -mt-2 flex flex-shrink-0 items-center px-3 py-2 text-xs font-medium text-blue-600"
							onClick={handleShowAll}
						>
							Visa mer produktinformation{' '}
							<ChevronDownIcon
								aria-hidden
								className="AccordionChevron ml-2 h-5 w-5 flex-shrink-0"
							/>
						</button>
					)}
					{!showMoreIsVisible && (
						<button
							type="button"
							aria-expanded={showAll}
							aria-controls={descriptionId}
							className="-ml-3 -mt-2 flex flex-shrink-0 items-center px-3 py-2 text-xs font-medium text-blue-600"
							onClick={handleHideAll}
						>
							Dölj produktinformation{' '}
							<ChevronUpIcon
								aria-hidden
								className="AccordionChevron ml-2 h-5 w-5 flex-shrink-0"
							/>
						</button>
					)}
				</div>
			</div>
		</div>
	)
}

function getImageSet(imageUrl: string, imageSizes: number[]) {
	// Starting point https://www.youtube.com/watch?v=4VUfk464vzk

	// Want to end up with something like this
	//res.cloudinary.com/internetmedicin/image/upload/f_auto/q_auto/c_scale/w_382/v1677165740/friends/Internetmedicin/Bro%CC%88stcancer-PierreFabre-1340x330-februari2023_m4da5y.png 382w,
	//res.cloudinary.com/internetmedicin/image/upload/f_auto/q_auto/c_scale/w_670/v1677165740/friends/Internetmedicin/Bro%CC%88stcancer-PierreFabre-1340x330-februari2023_m4da5y.png 670w,
	//res.cloudinary.com/internetmedicin/image/upload/f_auto/q_auto/c_scale/w_1340/v1677165740/friends/Internetmedicin/Bro%CC%88stcancer-PierreFabre-1340x330-februari2023_m4da5y.png 1340w"

	const rootUrl = 'https://res.cloudinary.com/internetmedicin/image/upload'
	const endImgUrl = imageUrl.replace(rootUrl, '') // => /f_auto/q_auto/v1677165740/friends/lorem.png

	return imageSizes.map(size => {
		return `${rootUrl}/c_scale/w_${size}${endImgUrl} ${size}w`
	})
}

function SingleAd({
	ad,
	className,
	imageSizes,
	sizes,
	supportedFormats,
}: {
	ad: SafeAd
	className: string
	imageSizes: number[]
	sizes: string
	supportedFormats: SupportedFormats[]
}) {
	const [impressionTracked, setImpressionTracked] = React.useState(false)

	const imageIntersectionRef = React.useRef(null)
	const imageIntersection = useIntersection(imageIntersectionRef, {
		root: null,
		rootMargin: '0px',
		threshold: 1,
	})

	const adWrapperIntersectionRef = React.useRef(null)
	const adWrapperIntersection = useIntersection(adWrapperIntersectionRef, {
		root: null,
		rootMargin: '0px',
		threshold: 0.01, // Trigger when at least 1% of the target is visible
	})

	const [impressionCount, setImpressionCount] = React.useState(0)
	const debouncedImpressionCount = useDebounce(impressionCount, 1000)
	const [inView, setInView] = React.useState(false)

	const isIdle = useIdle(AD_IDLE_TIME)

	const adDelay = ad.delay && ad.delay > 0 ? ad.delay * 1000 : DEFAULT_AD_DELAY

	React.useEffect(() => {
		// Triggers when debouncedImpressionCount changes (skip initial 0)
		if (debouncedImpressionCount === 0) return

		trackEvent(`FriendAssistent:${ad.id}:Impression`)
		trackAdEvent({
			eventName: 'ad_impression',
			adId: ad.id,
		})

		const timer = setTimeout(() => {
			setImpressionTracked(false)
		}, adDelay)

		return () => clearTimeout(timer)
		// eslint-disable-next-line react-hooks/exhaustive-deps -- only fire on new debounced impression
	}, [debouncedImpressionCount])

	React.useEffect(() => {
		// Track impression
		if (
			imageIntersection &&
			imageIntersection.intersectionRatio === 1 &&
			!impressionTracked &&
			!isIdle
		) {
			setImpressionCount(c => c + 1)
			setImpressionTracked(true)
		}
	}, [impressionTracked, imageIntersection, isIdle])

	React.useEffect(() => {
		// Lazy load image (to save bandwidth (cost))
		// 500ms delay avoids loading images during fast scroll-throughs
		if (
			adWrapperIntersection &&
			adWrapperIntersection.isIntersecting &&
			!inView
		) {
			const timer = setTimeout(() => {
				setInView(true)
			}, 500)
			return () => clearTimeout(timer)
		}
	}, [inView, adWrapperIntersection])

	const [imageUrl, imageHeight, imageWidth, format] =
		findPreferredImageDimension(ad, supportedFormats)

	if (!imageUrl || !imageHeight || !imageWidth || !format) {
		return null
	}

	const getSpc = () => {
		if (ad.hideSummaryOfProductCharacteristics) {
			return undefined
		}

		if (ad.spc) {
			return ad.spc
		}

		if (ad.summaryOfProductCharacteristics) {
			return {
				title: 'Förkortad produktinformation',
				// Intentional hardcode for older ads that does not have the new spc field.
				hideTitle: ad.url.includes('lilly'),
				text: {
					json: ad.summaryOfProductCharacteristics,
				},
			}
		}

		return undefined
	}

	const spc: SafeAd['spc'] | undefined = getSpc()

	return (
		<div
			itemScope
			itemType="https://schema.org/WPAdBlock"
			className={`flex-col items-center space-y-2 print:hidden ${className}`}
			ref={adWrapperIntersectionRef}
			data-nosnippet
		>
			<p
				data-nosnippet
				className="text-xs uppercase text-gray-500 dark:text-gray-400"
			>
				Annons
			</p>
			<div className="w-full">
				<a
					data-nosnippet
					itemProp="url"
					onClick={() => {
						trackEvent(`FriendAssistent:${ad.id}:Click`)
						trackAdEvent({
							eventName: 'ad_click',
							adId: ad.id,
						})
					}}
					target="_blank"
					href={addUtmContent(ad.url, format)}
					rel="noreferrer noopener"
					className="block w-full"
				>
					<div
						className="w-full"
						ref={imageIntersectionRef}
						style={{ aspectRatio: `${imageWidth}/${imageHeight}` }}
					>
						{inView && (
							<>
								{imageUrl.endsWith('.mp4') ? (
									<video autoPlay loop muted playsInline className="w-full">
										<source src={imageUrl} type="video/mp4" />
										Din webbläsare stödjer inte video.
									</video>
								) : (
									<img
										data-nosnippet
										itemProp="image"
										className="w-full"
										sizes={sizes}
										srcSet={getImageSet(imageUrl, imageSizes).join(', ')}
										src={imageUrl}
										alt=""
									/>
								)}
							</>
						)}
					</div>
				</a>
				{spc && <Spc paddingX="px-2" spc={spc} />}
			</div>
		</div>
	)
}

export { SingleAd }
